import { Column, RowData } from '@tanstack/react-table';
import { CSSProperties } from 'react';

declare module '@tanstack/react-table' {
  // Declaration merging needs the same type parameters as ColumnMeta, which
  // this augmentation does not use.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /**
     * Left padding in px, replacing the cell default. Use it for a column
     * that reserves a gutter for a control such as a row expander.
     */
    paddingLeft?: number;
  }
}

// Data columns never render narrower than this, unless their declared size is
// already smaller (a column must be allowed to shrink to its own size)
export const DEFAULT_MIN_COLUMN_SIZE = 180;

/**
 * Determines if a column should have a fixed width (not flex).
 * Fixed columns are: pinned columns OR columns with enableResizing: false
 */
export function isFixedColumn(
  columnDef: { enableResizing?: boolean },
  isPinned: string | false
): boolean {
  return isPinned !== false || columnDef.enableResizing === false;
}

/**
 * The one flex style for a table cell in a column - shared by the data cells,
 * the header cells and the loading skeleton cells, so a loading table renders
 * pixel-identical to a loaded one and nothing jumps when a request starts.
 */
export function getColumnFlexStyle<TData>(
  column: Column<TData, unknown>,
  additionalStyles?: CSSProperties
): CSSProperties {
  const isPinned = column.getIsPinned();
  const isFixed = isFixedColumn(column.columnDef, isPinned);

  // Fixed columns (pinned/non-resizable) use definition size, ignoring localStorage
  // Resizable columns use their current size (which may be user-resized)
  const size = isFixed
    ? (column.columnDef.size ?? column.getSize())
    : column.getSize();

  // Get minSize from column definition or use default; never let a column's
  // minimum exceed its own declared size, or the fitted widths overflow
  const columnId = column.id;
  const isSpecialColumn = columnId === 'select' || columnId === 'actions';
  const declaredSize = column.columnDef.size ?? size;
  const minSize =
    column.columnDef.minSize ??
    (isSpecialColumn ? size : Math.min(declaredSize, DEFAULT_MIN_COLUMN_SIZE));

  // Resizable data columns grow to fill the container so the table never
  // leaves a dead gutter; pinned/select/actions columns keep their fixed width
  const shouldGrow = !isFixed && !isSpecialColumn;

  const paddingLeft = column.columnDef.meta?.paddingLeft;

  return {
    width: `${size}px`,
    minWidth: `${minSize}px`,
    flexShrink: 0,
    flexGrow: shouldGrow ? 1 : 0,
    ...(paddingLeft === undefined ? {} : { paddingLeft: `${paddingLeft}px` }),
    ...additionalStyles,
  };
}
